import React from "react";
import {
  Box,
  MenuItem,
  Button,
  TextField,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveIcon from "@mui/icons-material/Remove";

function PromotionalTab({
  isPromptSubmitted,
  promotionalItems,
  setPromotionalItems,
  promotionalProducts = [],
  promotionalItemsByProduct = {},
}) {
  if (!isPromptSubmitted) {
    return (
      <Typography sx={{ color: "#666", fontSize: "14px" }}>
        Please describe the call and send the prompt in the first tab to unlock
        this section.
      </Typography>
    );
  }

  const handlePromotionalChange = (index, field, value) => {
    const updated = [...promotionalItems];
    updated[index] = { ...updated[index], [field]: value };

    // If product changed, clear item (optional but usually desired)
    if (field === "productName") {
      updated[index].item = "";
    }

    setPromotionalItems(updated);
  };

  const addPromotionalItem = () => {
    setPromotionalItems([
      ...promotionalItems,
      { productName: "", item: "", qty: 1 },
    ]);
  };

  const removePromotionalItem = (index) => {
    // ✅ If only one row, reset it instead of removing (so minus stays meaningful)
    if (promotionalItems.length === 1) {
      setPromotionalItems([{ productName: "", item: "", qty: 1 }]);
      return;
    }

    setPromotionalItems(promotionalItems.filter((_, i) => i !== index));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box
        sx={{
          backgroundColor: "#f9f9f9",
          borderRadius: "8px",
        }}
      >
        {promotionalItems.map((item, index) => (
          <Box
            key={index}
            sx={{
              p: 2,
              pb: 0,
            }}
          >
            {/* Product Name row with minus icon */}
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{ mb: 1, fontSize: "12px", fontWeight: 600, color: "#333" }}
              >
                Product Name
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <TextField
                  fullWidth
                  select
                  value={item.productName}
                  onChange={(e) =>
                    handlePromotionalChange(
                      index,
                      "productName",
                      e.target.value,
                    )
                  }
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "white",
                      "& fieldset": { borderColor: "#e0e0e0" },
                    },
                  }}
                >
                  <MenuItem value="">Select a product</MenuItem>
                  {promotionalProducts.map((product) => (
                    <MenuItem key={product} value={product}>
                      {product}
                    </MenuItem>
                  ))}
                </TextField>

                {/* ✅ Always visible minus */}
                <IconButton
                  size="small"
                  onClick={() => removePromotionalItem(index)}
                  sx={{
                    backgroundColor: "#E53935",
                    color: "white",
                    width: 20,
                    height: 20,
                    flexShrink: 0,
                    "&:hover": { backgroundColor: "#D32F2F" },
                  }}
                >
                  <RemoveIcon fontSize="20px" />
                </IconButton>
              </Box>
            </Box>

            {/* Item + Qty */}
            <Box
              sx={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 2 }}
            >
              <Box>
                <Box
                  sx={{
                    mb: 1,
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#333",
                  }}
                >
                  Item
                </Box>
                <TextField
                  fullWidth
                  select
                  value={item.item}
                  onChange={(e) =>
                    handlePromotionalChange(index, "item", e.target.value)
                  }
                  variant="outlined"
                  disabled={!item.productName}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "white",
                      "& fieldset": { borderColor: "#e0e0e0" },
                    },
                  }}
                >
                  <MenuItem value="">Select an item</MenuItem>
                  {(promotionalItemsByProduct[item.productName] || []).map(
                    (opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ),
                  )}
                </TextField>
              </Box>

              <Box>
                <Box
                  sx={{
                    mb: 1,
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#333",
                  }}
                >
                  Qty
                </Box>
                <TextField
                  fullWidth
                  type="number"
                  value={item.qty}
                  onChange={(e) =>
                    handlePromotionalChange(
                      index,
                      "qty",
                      Math.max(1, parseInt(e.target.value, 10) || 1),
                    )
                  }
                  variant="outlined"
                  inputProps={{ min: 1 }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "white",
                      "& fieldset": { borderColor: "#e0e0e0" },
                    },
                  }}
                />
              </Box>
            </Box>
            {promotionalItems.length > 1 &&
              index !== promotionalItems.length - 1 && (
                <Divider
                  sx={{
                    color: "#DADADA",
                    my: "1rem",
                  }}
                />
              )}
          </Box>
        ))}
        <Divider
          sx={{
            color: "#DADADA",
            margin: "1rem",
          }}
        />
        <Box p={2} pt={0}>
          <Button
            startIcon={<AddCircleOutlineIcon />}
            onClick={addPromotionalItem}
            sx={{
              textTransform: "none",
              color: "#ff8c42",
              fontWeight: 600,
              justifyContent: "flex-start",
              "&:hover": {
                backgroundColor: "transparent",
                textDecoration: "underline",
              },
            }}
          >
            Add New
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default PromotionalTab;
